import React, { useEffect, useMemo, useState } from 'react';
import { Clock } from 'lucide-react';

function toIntOrNull(v: string): number | null {
  if (v.trim() === '') return null;
  const n = Number.parseInt(v, 10);
  return Number.isFinite(n) ? n : null;
}

function clampNonNegative(n: number): number {
  return Number.isFinite(n) && n > 0 ? n : 0;
}

export const DurationSplitInput: React.FC<{
  /** Total minutes as a string ("" allowed) */
  value: string;
  /** Called with total minutes as a string ("" allowed) */
  onChange: (next: string) => void;
  /** If true, empty is allowed and will emit "" when both fields are blank */
  allowEmpty?: boolean;
  /** Display error state */
  error?: string;
  /** Mark as required in label (visual only) */
  required?: boolean;
  /** When true, do not render the internal label (e.g. when parent provides its own) */
  hideLabel?: boolean;
  /** When true with hideLabel, do not render the Total row (parent shows label and total on one line) */
  hideTotal?: boolean;
}> = ({ value, onChange, allowEmpty = false, error, required = false, hideLabel = false, hideTotal = false }) => {
  const derived = useMemo(() => {
    const minutesTotal = toIntOrNull(value);
    if (minutesTotal == null) return { hours: '', minutes: '' };
    const total = clampNonNegative(minutesTotal);
    return { hours: String(Math.floor(total / 60)), minutes: String(total % 60) };
  }, [value]);

  const [hours, setHours] = useState<string>(derived.hours);
  const [minutes, setMinutes] = useState<string>(derived.minutes);

  // Keep inputs in sync if the parent changes the total minutes (e.g., edit prefill / localStorage restore)
  useEffect(() => {
    setHours(derived.hours);
    setMinutes(derived.minutes);
  }, [derived.hours, derived.minutes]);

  const emit = (nextHours: string, nextMinutes: string) => {
    const h = toIntOrNull(nextHours);
    const m = toIntOrNull(nextMinutes);

    if (allowEmpty && (h == null && m == null)) {
      onChange('');
      return;
    }

    const total = clampNonNegative((h ?? 0) * 60 + (m ?? 0));
    onChange(String(total));
  };

  const handleHoursChange = (v: string) => {
    // allow clearing
    if (v === '') {
      setHours('');
      emit('', minutes);
      return;
    }
    // digits only
    if (!/^\d+$/.test(v)) return;
    setHours(v);
    emit(v, minutes);
  };

  const handleMinutesChange = (v: string) => {
    if (v === '') {
      setMinutes('');
      emit(hours, '');
      return;
    }
    if (!/^\d+$/.test(v)) return;

    const raw = Number.parseInt(v, 10);
    const safeRaw = clampNonNegative(raw);

    // Normalize minutes >= 60 into hours carry.
    const carry = Math.floor(safeRaw / 60);
    const nextMin = safeRaw % 60;
    const currentHours = toIntOrNull(hours) ?? 0;
    const nextHoursNum = currentHours + carry;

    const nextHoursStr = String(nextHoursNum);
    const nextMinutesStr = String(nextMin);

    setHours(nextHoursStr);
    setMinutes(nextMinutesStr);
    emit(nextHoursStr, nextMinutesStr);
  };

  const totalMinutesDisplay = (() => {
    const n = toIntOrNull(value);
    if (n == null) return null;
    return clampNonNegative(n);
  })();

  const showTopRow = hideLabel && hideTotal ? false : (hideLabel ? typeof totalMinutesDisplay === 'number' : true);

  return (
    <div>
      {showTopRow ? (
        <div className={`flex items-center ${hideLabel ? 'justify-end mb-1' : 'justify-between mb-2'}`}>
          {!hideLabel && (
            <label className="block text-sm font-medium text-gray-700">
              Duration{required ? ' *' : ''}
            </label>
          )}
          {!hideTotal && typeof totalMinutesDisplay === 'number' && (
            <span className="text-xs text-gray-500">Total: {totalMinutesDisplay} minutes</span>
          )}
        </div>
      ) : null}

      <div
        className={`w-full flex items-stretch overflow-hidden rounded-lg border bg-white ${hideLabel ? 'mt-2' : ''} ${
          error ? 'border-red-500' : 'border-gray-300'
        }`}
      >
        {/* Hours segment */}
        <div className="flex items-stretch flex-1 min-w-0">
          <div className="px-3 flex items-center justify-center bg-gray-50 border-r border-gray-200">
            <Clock className="h-4 w-4 text-gray-500" />
          </div>
          <input
            inputMode="numeric"
            pattern="[0-9]*"
            value={hours}
            onChange={(e) => handleHoursChange(e.target.value)}
            className="flex-1 min-w-0 px-3 py-2 border-0 bg-transparent outline-none focus:ring-2 focus:ring-[#0B4778] focus:ring-inset"
            placeholder="0"
            aria-label="Duration hours"
          />
          <div className="px-3 flex items-center justify-center text-sm text-gray-600 bg-gray-50 border-l border-gray-200">
            hr
          </div>
        </div>

        {/* Divider between segments */}
        <div className="w-px bg-gray-200" />

        {/* Minutes segment */}
        <div className="flex items-stretch flex-1 min-w-0">
          <input
            inputMode="numeric"
            pattern="[0-9]*"
            value={minutes}
            onChange={(e) => handleMinutesChange(e.target.value)}
            className="flex-1 min-w-0 px-3 py-2 border-0 bg-transparent outline-none focus:ring-2 focus:ring-[#0B4778] focus:ring-inset"
            placeholder="0"
            aria-label="Duration minutes"
          />
          <div className="px-3 flex items-center justify-center text-sm text-gray-600 bg-gray-50 border-l border-gray-200">
            min
          </div>
        </div>
      </div>

      {error && <p className="text-red-500 text-sm mt-1">{error}</p>}
    </div>
  );
};

